import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { guardianAlertService } from "../../../services/guardianAlertService";
import { openAlertDetails } from "../../../utils/appNavigation";
import { richTextToPlainText } from "../../../utils/richText";
import { StyledButtonForm } from "../DetailsArea/styles";
import {
  AlertsSection,
  AlertsSectionHeader,
  AlertsSectionTitle,
  AlertsCount,
  AlertsEmpty,
  AlertsList,
  AlertCard,
  AlertCardHeader,
  AlertCode,
  AlertBadges,
  Badge,
  AlertMetaGrid,
  AlertMetaItem,
  AlertMetaLabel,
  AlertMetaValue,
  AlertObs,
  TicketsBlock,
  TicketItem,
  TicketPhotos,
  TicketPhoto,
  AlertsActions,
} from "./alertsStyles";

const ACTION_LABELS = {
  criado: "Alerta criado",
  recebido: "Recebido",
  aguardando_resposta: "Aguardando resposta",
  resposta_rejeitado: "Resposta rejeitada",
  equipe_enviada: "Equipe enviada",
  respondido_processo_sucessao: "Respondido / Sucessão natural",
  encerrado: "Encerrado",
};

const SEVERITY_TONE = {
  Baixo: "ok",
  Medio: "warn",
  Médio: "warn",
  Grave: "danger",
};

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("pt-BR");
}

function formatCoordinates(coords) {
  if (!coords) return null;
  if (typeof coords === "string") return coords;
  if (Array.isArray(coords)) {
    return coords
      .map((c) => {
        if (Array.isArray(c) && c.length >= 2) {
          return `${Number(c[1]).toFixed(5)}, ${Number(c[0]).toFixed(5)}`;
        }
        if (c && typeof c === "object") {
          const lat = c.lat ?? c.latitude;
          const lng = c.lng ?? c.lon ?? c.longitude;
          if (lat != null && lng != null) {
            return `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`;
          }
        }
        return null;
      })
      .filter(Boolean)
      .join(" · ");
  }
  if (typeof coords === "object") {
    const lat = coords.lat ?? coords.latitude;
    const lng = coords.lng ?? coords.lon ?? coords.longitude;
    if (lat != null && lng != null) {
      return `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`;
    }
    try {
      return JSON.stringify(coords);
    } catch {
      return String(coords);
    }
  }
  return String(coords);
}

function latestTicketAction(tickets = []) {
  if (!tickets.length) return null;
  const sorted = [...tickets].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );
  return sorted[0]?.action || null;
}

/**
 * Lista todos os alertas da área (mais recentes primeiro) na aba Dados gerais.
 */
const AreaAlertsSection = ({ areaId }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!areaId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await guardianAlertService.getAlertHistoryTable(areaId, {
          page_size: 1000,
        });
        const list = Array.isArray(data) ? data : [];
        const ordered = [...list].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        if (!cancelled) setAlerts(ordered);
      } catch (err) {
        console.error("Erro ao carregar alertas da área:", err);
        if (!cancelled) {
          setAlerts([]);
          setError("Não foi possível carregar os alertas desta área.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [areaId]);

  const countLabel = useMemo(() => {
    const n = alerts.length;
    if (loading) return "Carregando…";
    return `${n} alerta${n === 1 ? "" : "s"}`;
  }, [alerts.length, loading]);

  const openAlert = (alert) => {
    const tickets = Array.isArray(alert.tickets) ? alert.tickets : [];
    const lastTicket = [...tickets].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    )[0];
    if (lastTicket?.ticket_id) {
      openAlertDetails(dispatch, navigate, {
        ticketId: lastTicket.ticket_id,
        codAlerta: alert.cod_alerta,
      });
      return;
    }
    if (alert.cod_alerta) {
      navigate(`/alerts/buscar/${alert.cod_alerta}`);
    }
  };

  return (
    <AlertsSection>
      <AlertsSectionHeader>
        <AlertsSectionTitle>Alertas da área</AlertsSectionTitle>
        <AlertsCount>{countLabel}</AlertsCount>
      </AlertsSectionHeader>

      <AlertsActions>
        <StyledButtonForm
          type="button"
          secondary
          onClick={() => navigate(`/alerts/history/${areaId}`)}
        >
          Ver histórico completo
        </StyledButtonForm>
      </AlertsActions>

      {loading ? (
        <AlertsEmpty>Carregando alertas…</AlertsEmpty>
      ) : error ? (
        <AlertsEmpty>{error}</AlertsEmpty>
      ) : alerts.length === 0 ? (
        <AlertsEmpty>Nenhum alerta registrado para esta área.</AlertsEmpty>
      ) : (
        <AlertsList>
          {alerts.map((alert, index) => {
            const tickets = Array.isArray(alert.tickets) ? alert.tickets : [];
            const lastAction = latestTicketAction(tickets);
            const coords = formatCoordinates(alert.coordinates);
            const observation = richTextToPlainText(alert.observation) || "";
            const severityTone = SEVERITY_TONE[alert.severity] || "neutral";

            return (
              <AlertCard key={alert.alert_id || alert.cod_alerta || index}>
                <AlertCardHeader>
                  <AlertCode
                    type="button"
                    onClick={() => openAlert(alert)}
                    title="Abrir detalhes do alerta"
                  >
                    #{index + 1} · {alert.cod_alerta || "Sem código"}
                  </AlertCode>
                  <AlertBadges>
                    {alert.severity ? (
                      <Badge $tone={severityTone}>{alert.severity}</Badge>
                    ) : null}
                    {alert.respondido != null ? (
                      <Badge $tone={alert.respondido ? "ok" : "warn"}>
                        {alert.respondido ? "Respondido" : "Em aberto"}
                      </Badge>
                    ) : null}
                    {lastAction ? (
                      <Badge $tone="neutral">
                        {ACTION_LABELS[lastAction] || lastAction}
                      </Badge>
                    ) : (
                      <Badge $tone="neutral">Criado</Badge>
                    )}
                  </AlertBadges>
                </AlertCardHeader>

                <AlertMetaGrid>
                  <AlertMetaItem>
                    <AlertMetaLabel>Tipo</AlertMetaLabel>
                    <AlertMetaValue>{alert.alert_type || "—"}</AlertMetaValue>
                  </AlertMetaItem>
                  <AlertMetaItem>
                    <AlertMetaLabel>Criado em</AlertMetaLabel>
                    <AlertMetaValue>
                      {formatDateTime(alert.created_at)}
                    </AlertMetaValue>
                  </AlertMetaItem>
                  <AlertMetaItem>
                    <AlertMetaLabel>Criado por</AlertMetaLabel>
                    <AlertMetaValue>{alert.created_by || "—"}</AlertMetaValue>
                  </AlertMetaItem>
                  <AlertMetaItem>
                    <AlertMetaLabel>Tickets / ações</AlertMetaLabel>
                    <AlertMetaValue>{tickets.length}</AlertMetaValue>
                  </AlertMetaItem>
                  {coords ? (
                    <AlertMetaItem data-full="true">
                      <AlertMetaLabel>Coordenadas</AlertMetaLabel>
                      <AlertMetaValue>{coords}</AlertMetaValue>
                    </AlertMetaItem>
                  ) : null}
                  {observation ? (
                    <AlertMetaItem data-full="true">
                      <AlertMetaLabel>Observação</AlertMetaLabel>
                      <AlertObs title={observation}>{observation}</AlertObs>
                    </AlertMetaItem>
                  ) : null}
                </AlertMetaGrid>

                {tickets.length > 0 && (
                  <TicketsBlock>
                    <AlertMetaLabel>Histórico de ações</AlertMetaLabel>
                    {[...tickets]
                      .sort(
                        (a, b) =>
                          new Date(b.created_at) - new Date(a.created_at)
                      )
                      .map((ticket) => {
                        const ticketObs =
                          richTextToPlainText(ticket.observations) || "";
                        const photos = Array.isArray(ticket.photos)
                          ? ticket.photos
                          : [];
                        return (
                          <TicketItem key={ticket.ticket_id}>
                            <div>
                              <strong>
                                {ACTION_LABELS[ticket.action] ||
                                  ticket.action ||
                                  "Ação"}
                              </strong>
                              <span>
                                {" "}
                                · {formatDateTime(ticket.created_at)}
                                {ticket.created_by
                                  ? ` · ${ticket.created_by}`
                                  : ""}
                              </span>
                            </div>
                            {ticketObs ? <AlertObs>{ticketObs}</AlertObs> : null}
                            {ticket.responses ? (
                              <AlertObs>
                                Resposta:{" "}
                                {typeof ticket.responses === "string"
                                  ? ticket.responses
                                  : JSON.stringify(ticket.responses)}
                              </AlertObs>
                            ) : null}
                            {photos.length > 0 ? (
                              <TicketPhotos>
                                {photos.map((photo) =>
                                  photo.image_url ? (
                                    <TicketPhoto
                                      key={photo.id}
                                      href={photo.image_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title={photo.type || "Foto"}
                                    >
                                      <img
                                        src={photo.image_url}
                                        alt={photo.type || "Foto do ticket"}
                                      />
                                    </TicketPhoto>
                                  ) : null
                                )}
                              </TicketPhotos>
                            ) : null}
                          </TicketItem>
                        );
                      })}
                  </TicketsBlock>
                )}

                <AlertsActions style={{ marginTop: "0.75rem" }}>
                  <StyledButtonForm
                    type="button"
                    secondary
                    onClick={() => openAlert(alert)}
                  >
                    Abrir alerta
                  </StyledButtonForm>
                </AlertsActions>
              </AlertCard>
            );
          })}
        </AlertsList>
      )}
    </AlertsSection>
  );
};

export default AreaAlertsSection;
